import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ACTION_COLORS = {
  resume_parsing: '#00A0E0', candidate_ranking: '#F05000', assessment_generation: '#059669',
  ai_questions: '#D97706', video_interview: '#DC2626',
};
const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (Number(v) || 0).toLocaleString();
const cr = (v) => (v === null || v === undefined ? '—' : `${(Number(v)).toLocaleString(undefined, { maximumFractionDigits: 1 })}`);

function Kpi({ label, value, hint, tone = '#002A38', big, testId }) {
  return (
    <Card data-testid={testId} style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B' }}>{label}</div>
      <div style={{ fontSize: big ? '2.25rem' : '1.75rem', fontWeight: 800, fontFamily: 'Cabinet Grotesk', color: tone, marginTop: '0.25rem', lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>{hint}</div>}
    </Card>
  );
}

function csvExport(data, scope) {
  const rows = [['Job', 'Company', 'Candidates processed', 'Hires', 'Total credits', 'Avg credits/candidate', 'Avg credits/hire', 'Est. cost', 'Cost/hire (USD)']];
  (data.jobs || []).forEach((j) => {
    rows.push([j.job_title || j.job_id, j.company_slug || '', j.candidates, j.hires, j.credits,
      j.avg_credits_per_candidate ?? '', j.avg_credits_per_hire ?? '', (j.cost ?? 0).toFixed(2), j.cost_per_hire ?? '']);
  });
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chire-cost-per-hire-${scope}-${data.month || 'all-time'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function JobCostInsight({ getReport, showCompany = false, intro }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await getReport(m || undefined);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load job cost insight');
    } finally {
      setLoading(false);
    }
  }, [getReport]);

  useEffect(() => { load(month); }, [month, load]);

  if (loading && !data) return <div style={{ color: '#64748B', padding: '2rem' }}>Loading cost per hire…</div>;
  if (!data) return null;

  const totals = data.totals || {};
  const jobs = data.jobs || [];
  const cpc = data.cost_per_credit;

  return (
    <div data-testid="job-cost-insight" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 620, margin: 0, lineHeight: 1.6 }}>
          {intro || 'Average AI credits it takes to move one candidate from resume upload to hire, per job. Avg credits / hire = total AI credits spent on the job ÷ candidates hired.'}
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.25rem' }}>Period</label>
            <select data-testid="job-cost-month-select" value={month} onChange={(e) => setMonth(e.target.value)}
              style={{ padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.8125rem' }}>
              <option value="">All time</option>
              {(data.months_available || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <Button data-testid="job-cost-export-csv" onClick={() => csvExport(data, showCompany ? 'admin' : 'team')} variant="outline" style={{ borderRadius: 8 }}>Export CSV</Button>
        </div>
      </div>

      {/* KPIs — cost per hire headline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <Kpi testId="kpi-cost-per-hire" label="Avg credits / hire" value={totals.hires ? cr(totals.avg_credits_per_hire) : '—'} tone="#F05000" big hint={totals.hires ? `across ${num(totals.hires)} hires` : 'no hires recorded yet'} />
        <Kpi testId="kpi-cost-per-candidate" label="Avg credits / candidate" value={cr(totals.avg_credits_per_candidate)} tone="#00A0E0" hint={`${num(totals.candidates)} candidates`} />
        <Kpi testId="kpi-total-hires" label="Hires" value={num(totals.hires)} hint={`${num(totals.jobs)} jobs`} />
        <Kpi testId="kpi-cost-total" label="Total credits" value={num(totals.credits)} tone="#059669" hint={`${money(totals.cost)} @ ${money(cpc)}/cr`} />
      </div>

      {/* Per-job table */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Cost per hire by job{month ? ` — ${month}` : ''}</h3>
        <p style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '1rem' }}>“—” under Avg credits / hire means no candidate has been marked hired on that job yet.</p>
        {jobs.length === 0 ? (
          <p data-testid="job-cost-empty" style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>No job-attributed AI usage recorded yet for this period.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748B', fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.5rem' }}>Job</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Candidates</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Hires</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total credits</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Cr / candidate</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', color: '#F05000' }}>Cr / hire</th>
                  <th style={{ padding: '0.5rem' }}>Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.job_id} data-testid="job-cost-row" style={{ borderTop: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '0.65rem 0.5rem' }}>
                      <div style={{ fontWeight: 700, color: '#002A38', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.job_title || 'Untitled job'}</div>
                      {showCompany && j.company_slug && <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{j.company_slug}</div>}
                    </td>
                    <td style={{ padding: '0.65rem 0.5rem', textAlign: 'right', color: '#334155' }}>{num(j.candidates)}</td>
                    <td style={{ padding: '0.65rem 0.5rem', textAlign: 'right', fontWeight: 700, color: j.hires ? '#059669' : '#94A3B8' }}>{num(j.hires)}</td>
                    <td style={{ padding: '0.65rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{num(j.credits)}</td>
                    <td style={{ padding: '0.65rem 0.5rem', textAlign: 'right', color: '#00A0E0', fontWeight: 600 }}>{cr(j.avg_credits_per_candidate)}</td>
                    <td data-testid="job-cost-per-hire-cell" style={{ padding: '0.65rem 0.5rem', textAlign: 'right', fontWeight: 800, color: j.hires ? '#F05000' : '#CBD5E1', fontFamily: 'Cabinet Grotesk' }}>{j.hires ? cr(j.avg_credits_per_hire) : '—'}</td>
                    <td style={{ padding: '0.65rem 0.5rem' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {Object.entries(j.by_action || {}).map(([at, ba]) => (
                          <span key={at} title={`${ba.label}: ${ba.actions} actions · ${ba.credits} credits`}
                            style={{ fontSize: '0.625rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 999, background: `${ACTION_COLORS[at] || '#94A3B8'}14`, color: ACTION_COLORS[at] || '#64748B' }}>
                            {(ba.label || at).split(' ')[0]} {num(ba.credits)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
